import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { StoreWithPlan, PlanFeatures } from '../types/planFeatures';

interface Store {
  id: string;
  name: string;
  location: string | null;
  city: string | null;
}

interface StoreResponse {
  store: StoreWithPlan;
}

interface StoresResponse {
  stores: Store[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Query keys for store-related data
 */
export const storeKeys = {
  all: ['stores'] as const,
  lists: () => [...storeKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...storeKeys.lists(), filters] as const,
  details: () => [...storeKeys.all, 'detail'] as const,
  detail: (id: string) => [...storeKeys.details(), id] as const,
  planFeatures: (id: string) => [...storeKeys.detail(id), 'planFeatures'] as const,
};

/**
 * Fetch a single store with plan features
 */
async function fetchStore(storeId: string): Promise<StoreWithPlan> {
  const response = await api.get<StoreResponse>(`/stores/${storeId}`);
  return response.data.store;
}

/**
 * Fetch all stores
 */
async function fetchStores(): Promise<Store[]> {
  const response = await api.get<StoresResponse>('/stores');
  return response.data.stores || [];
}

/**
 * Hook to fetch store data with caching
 * Automatically caches the data and revalidates in the background
 */
export function useStore(storeId: string | null | undefined) {
  return useQuery({
    queryKey: storeKeys.detail(storeId || ''),
    queryFn: () => fetchStore(storeId!),
    enabled: !!storeId,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes (formerly cacheTime)
  });
}

/**
 * Hook to fetch plan features for a store
 * Returns just the plan features portion of the store data
 */
export function usePlanFeatures(storeId: string | null | undefined) {
  const { data: store, isLoading, error } = useStore(storeId);
  
  return {
    planFeatures: store?.planFeatures || null,
    planName: store?.planName || null,
    isTrial: store?.isTrial || false,
    loading: isLoading,
    error: error ? (error as Error).message : null,
  };
}

/**
 * Hook to fetch all stores
 * Used for admin dropdowns and selection
 */
export function useStores() {
  return useQuery({
    queryKey: storeKeys.lists(),
    queryFn: fetchStores,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
}

/**
 * Hook to invalidate store cache
 * Call this after mutations that affect store data
 */
export function useInvalidateStore() {
  const queryClient = useQueryClient();
  
  return {
    invalidateStore: (storeId: string) => {
      queryClient.invalidateQueries({ queryKey: storeKeys.detail(storeId) });
    },
    invalidateAllStores: () => {
      queryClient.invalidateQueries({ queryKey: storeKeys.all });
    },
    refetchStore: (storeId: string) => {
      queryClient.refetchQueries({ queryKey: storeKeys.detail(storeId) });
    },
  };
}

/**
 * Hook to prefetch store data
 * Useful for prefetching on hover
 */
export function usePrefetchStore() {
  const queryClient = useQueryClient();
  
  return (storeId: string) => {
    queryClient.prefetchQuery({
      queryKey: storeKeys.detail(storeId),
      queryFn: () => fetchStore(storeId),
      staleTime: 5 * 60 * 1000,
    });
  };
}
